import Hls from "hls.js";

type VideoSourceOptions = {
  video: HTMLVideoElement;
  url: string;
  createHls: () => Hls;
  onReady: () => void;
  onHealthy: () => void;
  onBandwidthEstimate?: (bitsPerSecond: number) => void;
  onMediaBitrateEstimate?: (bitsPerSecond: number) => void;
  onRetry: (message: string) => void;
  onFatal: (detail: string) => void;
};

export type VideoSourceDisposer = (() => void) & {
  recover: () => boolean;
};

// Own all source events in one lifetime. Events from a disposed source must
// never downgrade the quality selected for the next source.
export const attachVideoSource = (options: VideoSourceOptions) => {
  const { video, url, onReady, onHealthy, onRetry, onFatal } = options;
  let active = true;
  let failed = false;
  let ready = false;
  let hls: Hls | null = null;
  let networkRetries = 0;
  let mediaRetries = 0;
  let decodedFrameTimer: ReturnType<typeof setTimeout> | null = null;

  const fatal = (detail: string) => {
    if (!active || failed) return;
    failed = true;
    onFatal(detail);
  };
  const loaded = () => {
    if (!active || failed || ready) return;
    ready = true;
    onReady();
  };
  const hasDecodedVideoFrame = () => video.videoWidth > 0 && video.videoHeight > 0;
  const healthy = () => {
    if (!active || failed || !hasDecodedVideoFrame()) return;
    if (decodedFrameTimer) {
      clearTimeout(decodedFrameTimer);
      decodedFrameTimer = null;
    }
    onHealthy();
  };
  const verifyDecodedFrame = () => {
    if (!active || failed || video.paused || hasDecodedVideoFrame()) {
      healthy();
      return;
    }
    if (decodedFrameTimer) return;
    decodedFrameTimer = setTimeout(() => {
      decodedFrameTimer = null;
      if (!active || failed || video.paused || hasDecodedVideoFrame()) {
        healthy();
        return;
      }
      fatal("音频已播放，但未检测到可解码的视频画面");
    }, 5000);
  };
  const mediaError = () => {
    // HLS.js owns MSE errors and recovery. Do not also trigger a second
    // fallback from the DOM error event for the very same failure.
    if (hls || !video.error || video.error.code === 1) return;
    fatal(`媒体错误 ${video.error.code}`);
  };

  video.addEventListener("loadedmetadata", loaded);
  video.addEventListener("canplay", healthy);
  video.addEventListener("playing", verifyDecodedFrame);
  video.addEventListener("resize", healthy);
  video.addEventListener("error", mediaError);

  const isHls = /\.m3u8(?:$|[?#])/i.test(url);
  // Chromium can report native HLS support yet fail to play TS playlists.
  // Prefer the MSE player; native HLS is only the compatibility fallback.
  if (isHls && Hls.isSupported()) {
    hls = options.createHls();
    hls.on(Hls.Events.FRAG_LOADED, (_event, data) => {
      // Disk cache speed is not network bandwidth and must not drive quality
      // upgrades or make the network monitor report an inflated Mbps value.
      const networkDetails = data.networkDetails as
        | { getResponseHeader?: (name: string) => string | null }
        | undefined;
      if (networkDetails?.getResponseHeader?.("X-Playback-Cache") === "hit") return;
      const loaded = Number(data.frag.stats?.loaded) || 0;
      const started = Number(data.frag.stats?.loading?.start) || 0;
      const ended = Number(data.frag.stats?.loading?.end) || 0;
      const elapsedMs = ended - started;
      if (loaded > 0 && elapsedMs > 0) {
        options.onBandwidthEstimate?.((loaded * 8 * 1000) / elapsedMs);
      }
      const mediaDuration = Number(data.frag.duration) || 0;
      if (loaded > 0 && mediaDuration > 0) {
        options.onMediaBitrateEstimate?.((loaded * 8) / mediaDuration);
      }
    });
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!active || failed || !data.fatal) return;
      const status = data.response?.code || 0;
      const permanent = status >= 400 && status < 500 && status !== 408 && status !== 429;
      if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !permanent && networkRetries < 2) {
        networkRetries += 1;
        onRetry(`播放连接中断，正在重试（${networkRetries}/2）…`);
        if (data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
            data.details === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT) {
          hls!.loadSource(url);
        } else {
          hls!.startLoad(Math.max(0, video.currentTime || 0));
        }
        return;
      }
      if (data.type === Hls.ErrorTypes.MEDIA_ERROR && mediaRetries < 1) {
        mediaRetries += 1;
        onRetry("正在恢复播放内核…");
        hls!.recoverMediaError();
        return;
      }
      fatal(`${data.details}${status ? ` · HTTP ${status}` : ""}`);
    });
    hls.loadSource(url);
    hls.attachMedia(video);
  } else if (!isHls || video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = url;
    video.load();
  } else {
    fatal("浏览器不支持 HLS 播放");
  }

  const dispose = (() => {
    active = false;
    video.removeEventListener("loadedmetadata", loaded);
    video.removeEventListener("canplay", healthy);
    video.removeEventListener("playing", verifyDecodedFrame);
    video.removeEventListener("resize", healthy);
    video.removeEventListener("error", mediaError);
    if (decodedFrameTimer) clearTimeout(decodedFrameTimer);
    hls?.destroy();
    video.removeAttribute("src");
    video.load();
  }) as VideoSourceDisposer;
  dispose.recover = () => {
    if (!active || failed || !hls) return false;
    networkRetries = 0;
    hls.stopLoad();
    hls.startLoad(Math.max(0, video.currentTime || 0));
    return true;
  };
  return dispose;
};
